<template>
  <page ref="pageRef">
    <loader v-if="loading" />
    <template v-else>
      <div class="container" v-if="productTypeId === ProductIds.REFI">
        <div class="row content flex-column-reverse flex-lg-row">
          <div class="col-12 col-lg-6 col-xl-6 text-left cuscd-item" id="private-line">
            <application-status
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @todoListClicked="scrollToTodoList"
            />
          </div>
          <div class="col-12 col-lg-6 cuscd-item" :class="[showApplicationInfoTile ? '' : 'hide']">
            <application-info
              id="applicationInformation"
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @complete="stepComplete"
              @hydrateStore="hydrateStore"
            />
          </div>
        </div>
        <div class="row content flex-lg-row checklist-wrapper">
          <div
            class="col-12 col-lg-4 col-xl-4 cuscd-item"
            id="app-todolist-income"
            :class="[showTodoListTile ? '' : 'hide']"
          >
            <application-checklist-income
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @hydrateStore="hydrateStore"
            />
          </div>
          <div
            class="col-12 col-lg-4 col-xl-4 cuscd-item"
            id="app-todolist"
            v-show="nonIncomeStipsExist && showTodoListTile"
          >
            <application-checklist
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @hydrateStore="hydrateStore"
            />
          </div>
          <div class="col-12 col-lg-4 col-xl-4 cuscd-item" id="applicationFileUpload">
            <application-file-upload
              v-if="showApplicationFileUpload"
              :application="appData.application"
              :applicant="appData.currentApplicant"
            />
            <application-reapply
              :application="appData.application"
              :applicant="appData.currentApplicant"
            />
          </div>
        </div>
      </div>
      <ApplicationPEL
        v-else-if="productTypeId === ProductIds.PEL"
        :showApplicationInfoTile="showApplicationInfoTile"
        :showApplicationFileUpload="showApplicationFileUpload"
        :showTodoListTile="showTodoListTile"
        @complete="stepComplete"
        @hydrateStore="hydrateStore"
        @viewCertificationUpdates="scrollToCertificationUpdates"
      />
      <div class="container" v-else>
        <div class="row content flex-column-reverse flex-lg-row">
          <div class="col-12 col-lg-3 col-xl-3 sidenav cuscd-item">
            <application-info
              v-if="showApplicationInfoTile"
              id="applicationInformation"
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @complete="stepComplete"
              @hydrateStore="hydrateStore"
            />
            <application-file-upload
              v-if="showApplicationFileUpload"
              id="applicationFileUpload"
              :application="appData.application"
              :applicant="appData.currentApplicant"
            />
            <application-reapply />
          </div>
          <div class="col-12 col-lg-4 col-xl-5 text-left cuscd-item" id="private-line">
            <application-status @viewCertificationUpdates="scrollToCertificationUpdates" />
          </div>
          <div
            class="col-12 col-lg-5 col-xl-4 sidenav cuscd-item cuscd-app-side"
            id="app-todolist"
            :class="[showTodoListTile ? '' : 'hide']"
          >
            <application-checklist
              :application="appData.application"
              :applicant="appData.currentApplicant"
              @hydrateStore="hydrateStore"
            />
          </div>
        </div>
      </div>
      <div class="disclosures" v-if="showCCPAForm">
        <california-consumer-privacy-act />
      </div>
    </template>
  </page>
</template>
<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import useApplications from "@/use/application";
import useFormatter from "@/use/formatter";
import Page from "@/components/shared/layout/Page.vue";
import ApplicationInfo from "@/components/ApplicationInfo.vue";
import ApplicationStatus from "@/components/ApplicationStatus.vue";
import ApplicationChecklist from "@/components/ApplicationChecklist.vue";
import ApplicationChecklistIncome from "@/components/ApplicationChecklistIncome.vue";
import ApplicationFileUpload from "@/components/ApplicationFileUpload.vue";
import ApplicationReapply from "@/components/shared/ApplicationReapply.vue";
import Loader from "@/components/shared/Loader.vue";
import { useStore } from "vuex";
import * as jsonPatch from "fast-json-patch";
import { $api } from "@/services/api";
import type { Application, Applicant, AppData } from "@/models/application";
import type { LoanVM, Stipulation } from "@/models/opal";
import { ProductIds } from "@/config/constants";
import { ApplicationStatusTypeIds } from "@/config/constants";
import CaliforniaConsumerPrivacyAct from "@/components/disclosures/CaliforniaConsumerPrivacyAct.vue";
import ApplicationPEL from "@/components/application-views/ApplicationPEL.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";

const store = useStore();
const route = useRoute();
const { getUserApplications } = useApplications();
const { formattedDateFormat } = useFormatter();

const pageRef = ref<InstanceType<typeof Page> | null>(null);

let applicationStips = reactive([] as Stipulation[]);
const productTypeId = computed(() => store?.state?.appData?.application?.productTypeId);

// Data
const showCCPA = ref<boolean>(false);
const activeApplication = ref<undefined | Application>(undefined);
const applicationStatus = ref<undefined | string>(undefined);
const applicants = ref<undefined | Array<Applicant>>(undefined);
const applicant = ref<undefined | Applicant>(undefined);
const applicationStatusName = ref<undefined | string>(undefined);
const loading = ref<boolean>(true);
const isPostDisbursement = ref<boolean>(false);
let appObserver = reactive({} as jsonPatch.Observer<Application>);

const currentApplicationId = computed<number | undefined>(() => appData.value.application?.id);
const showCCPAForm = computed(() => (store.getters["showCCPA"] && oidcUser.value) || false);
const Loan = computed<LoanVM>(() => store.getters["fundsModule/Loan"]);
const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const oidcIsAuthenticated = computed(() => store.getters["oidcStore/oidcIsAuthenticated"]);
const applications = computed(() => store.state?.applications);
const appData = computed<AppData>(() => store.state?.appData);
const loanBoarded = computed<boolean>(() =>
  appData.value?.application?.loanId &&
  appData.value?.application?.loanId > 0 &&
  Loan.value?.loanBoarded
    ? true
    : false,
);
const applicationStatusId = computed(() => appData.value?.application?.applicationStatusId);
const sortedItemsState = computed(() => store.state.checklistModule.sortedItems);
const nonIncomeStipsExist = computed(() => {
  if (sortedItemsState?.value && sortedItemsState?.value?.length > 0) {
    let stips = sortedItemsState?.value?.find((s: any) => s.title !== "Income Verification")?.items;
    return stips?.length > 0;
  } else {
    return false;
  }
});
const showApplicationDownloadTile = computed<boolean>(() => {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING:
    case ApplicationStatusTypeIds.COMPLETE:
      return true;
    default:
      return false;
  }
});
const showTodoListTile = computed<boolean>(() => {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.NOT_SUBMITTED:
    case ApplicationStatusTypeIds.DECLINED:
    case ApplicationStatusTypeIds.SMR_DECLINED:
      return false;
    default:
      return true;
  }
});
const showApplicationInfoTile = computed<boolean>(() => {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.NOT_SUBMITTED:
    case ApplicationStatusTypeIds.DECLINED:
    case ApplicationStatusTypeIds.SMR_DECLINED:
      return false;
    default:
      return true;
  }
});
const showApplicationFileUpload = computed<boolean>(() => {
  switch (applicationStatusId.value) {
    case ApplicationStatusTypeIds.COMPLETE:
      if (productTypeId.value === ProductIds.LOC && loanBoarded.value) {
        return false;
      }
      if (productTypeId.value === ProductIds.REFI && isPostDisbursement.value) {
        return false;
      }
      return true;
    default:
      return true;
  }
});
const { editingFromConfirmYourLoanDetails } = useApplicationInfo();
// Methods
function scrollToTodoList() {
  // check if mobile and REFI application
  if (window.innerWidth <= 991 && productTypeId.value === ProductIds.REFI) {
    const element = document.getElementById("app-todolist-income");
    element?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }
}

function scrollToCertificationUpdates() {
  const element = document.getElementById("certification-updates");
  element?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
}

async function stepComplete(data: any) {
  loading.value = true;
  await updateAppData();
}
async function updateAppData(person: any = undefined) {
  // 1. Gather the Application Patch object
  console.log("Gather the Application Patch object", appData.value?.application);
  const applicationPatch = jsonPatch.generate(appObserver);
  const updatedAppData = await $api.applications.updateApplication(
    appData.value?.currentApplicant?.userId ?? "",
    appData.value?.id,
    applicationPatch,
    editingFromConfirmYourLoanDetails.value,
  );
  console.log("Application.vue updateAppData() UPDATED APP", updatedAppData);
  if (!updatedAppData.error || !updatedAppData.id) {
    await store.dispatch("setAppDataApplicationAndApplicants", updatedAppData);
    activeApplication.value = appData.value?.application;
  } else {
    alert("Something has gone wrong");
  }
}
async function hydrateStore() {
  if (applications?.value && applications?.value?.length <= 0) {
    const applications = await getUserApplications(oidcUser?.value?.sub);
    await store.dispatch("setApplications", applications);
  }
  const response = await $api.applications.getUserApplicationByRefId(
    oidcUser?.value?.sub,
    route?.params?.referenceId as string,
    0,
  );
  if (response.currentApplicant && response.appData) {
    applicant.value = response.currentApplicant[0];
    activeApplication.value = response.appData.application;
    applicants.value = response.appData.applicants;
  }
  if (activeApplication.value) {
    applicationStatusName.value = activeApplication.value?.applicationStatusName;
    let applicationStipsResponse = await $api.applications.getApplicationStips(
      activeApplication.value?.id,
    );
    const applicationTypeId = activeApplication.value?.metadata?.withCoApplicants === true ? 2 : 1;
    const incomeLimit =
      applicationTypeId === 1 ? "BorrowerMinIncomeCondApproved" : "CoBorrowerMinIncomeCondApproved";
    const limitTypeId = applicationTypeId === 1 ? 9 : 28;
    const dateToCheck = activeApplication.value?.submittedAtUtc
      ? formattedDateFormat(activeApplication.value?.submittedAtUtc, "YYYY-MM-DD")
      : activeApplication.value?.submittedAtUtc;
    if (dateToCheck !== null) {
      const incomeLimitExists = await $api.programpricing.incomeLimitCheck(
        activeApplication.value?.lenderId as number,
        activeApplication.value?.programTypeId,
        activeApplication.value?.productTypeId,
        limitTypeId,
        dateToCheck as Date,
      );
      store.dispatch("checklistModule/setShowIncomeVerification", incomeLimitExists);
    }
    if ((applicationStipsResponse?.results || []).length > 0) {
      applicationStips = applicationStipsResponse?.results;
    }
  }
  console.log("Active App", activeApplication?.value);
  await store.dispatch("setAppData", {
    applicants: applicants?.value,
    application: activeApplication?.value,
    currentApplicant: applicant?.value,
    applicationStips: applicationStips,
    applicantDocuments: [],
    applicationDocuments: [],
  });
  await store.dispatch("lookupsModule/getLookups");
  console.log("appData", appData.value);
}
async function validatePostDisbursment() {
  if (
    productTypeId.value === ProductIds.REFI &&
    appData.value.application.loanId &&
    appData.value.application.loanId > 0
  ) {
    const response = await $api.applications.getPayoffDisbursementStatus(
      appData.value.application.loanId,
    );
    if (response?.success && response?.results) {
      isPostDisbursement.value = response?.results.loanComplete;
    }
  }
}
function refreshObservers() {
  if (appData.value) {
    if (appData.value?.application) {
      appObserver = jsonPatch.observe(appData.value?.application);
    }
  }
}

async function getLenderConfiguration() {
  loading.value = true;

  const lenderConfig = await $api.configuration.getLenderConfiguration(
    appData.value?.application?.programTypeId || 0,
    appData.value?.application?.productTypeId || 0,
    appData.value?.application?.lenderId ?? 0,
  );

  if (lenderConfig.lenderData) {
    await store.dispatch("setLenderData", lenderConfig.lenderData);

    store.dispatch("applicationsModule/setLenderId", lenderConfig.lenderData.lid);

    localStorage.setItem("slc-lid", lenderConfig.lenderData.lid);
  } else {
    await store.dispatch("setDefaultLender");
  }

  const newConfig = {
    ...store.state.configuration,
    ...lenderConfig,
    borrowerApplicationUrl: lenderConfig.borrowerApplicationUrl,
    docUploadUrl: `${lenderConfig.docUploadUrl}/api/v1/DocUploadService`,
    identityUrl: lenderConfig?.identityUrl,
    payOffSample: lenderConfig?.payOffSample,
    loanAccountSample: lenderConfig?.loanAccountSample,
    collegeCounselorSchedulerUrl: lenderConfig?.collegeCounselorSchedulerUrl,
  };
  store.dispatch("setConfiguration", newConfig);

  loading.value = false;
}

watch(
  () => appData.value,
  (value: any) => {
    console.log("Application Update");
    refreshObservers();
  },
);

watch(
  () => appData.value?.application,
  (value: any) => {
    activeApplication.value = value;
  },
  {
    deep: true,
  },
);

watch(
  () => currentApplicationId.value,
  async () => {
    loading.value = true;
    await getLenderConfiguration();
    loading.value = false;
  },
);

watch(
  () => showCCPAForm.value,
  (value: any) => {
    showCCPA.value = value;
  },
);

watch(
  () => loading.value,
  (value: boolean) => {
    pageRef.value?.setLoading(value);
  },
);

onMounted(async () => {
  pageRef.value?.setLoading(true);

  // rehydrate vuex stores
  await hydrateStore();
  await validatePostDisbursment();

  store.dispatch("applicationsModule/setLenderId", appData.value?.application?.lenderId || 0);
  loading.value = false;
  pageRef.value?.setLoading(false);
});

onBeforeUnmount(() => {
  console.log("Application.vue onBeforeUnmount");

  // Clear checklist data
  store.dispatch("checklistModule/resetChecklist");
});
</script>
<style lang="scss" scoped>
.disclosures {
  margin: auto;
  display: block;
  margin: 16px 30px;
}
.row {
  flex-wrap: nowrap;
}
#app-todolist {
  scroll-margin-top: 60px;
}
.checklist-wrapper {
  flex-wrap: wrap;
  border: 5px solid var(--theme-color);
  &.hide {
    border: none;
  }
}
</style>
